import * as THREE from 'three';

/**
 * A cheap depth-only prepass that captures the solid scene's depth into a `DepthTexture`,
 * so transparent effects can read how far away the geometry behind each pixel is.
 *
 * Built for {@link CloudSea}'s SOFT PARTICLES (2026-07-17, user: the cloud sprites look
 * "so buggy" where they collide with the models). A billboard is a flat quad, so where it
 * intersects a mesh the GPU cuts it along a razor-sharp line — the giveaway that the cloud
 * is cardboard. Knowing the scene's depth lets each puff fade out as it APPROACHES the
 * surface, dissolving into it like real vapour instead of slicing through.
 *
 * Normally this depth would come free from a postprocessing pass, but this project renders
 * DIRECTLY to the canvas (`settings.postprocessing.enabled` is false, because the composer's
 * OutputPass re-tone-maps the baked atlases) — hence a dedicated pass.
 *
 * Kept cheap on purpose, since everything here must survive a weak/software GPU:
 *  - renders at a fraction of canvas resolution (`settings.cloudSea.soft.resolutionScale`);
 *  - swaps in a trivial `overrideMaterial`, so no real shader runs — crucially this skips
 *    the emblem orb's raymarch, which would be brutal to run twice a frame;
 *  - skips objects that don't matter to the clouds (see `render`'s `exclude` argument).
 */
export class SceneDepth {
  readonly target: THREE.WebGLRenderTarget;
  /** The captured depth — bind this to a shader's sampler2D. */
  readonly texture: THREE.DepthTexture;

  /**
   * Colour output is irrelevant; this exists only so the depth attachment gets filled without
   * running any of the scene's real (potentially very expensive) shaders.
   *
   * ⚠️ It has NO map and NO alphaTest, so it renders every mesh as SOLID — an alpha-cutout
   * mesh (the vegetation planes) writes its full rectangle here, not its leaf shape. Anything
   * cut-out or transparent must therefore be passed to `render`'s `exclude` list, or effects
   * reading this depth will treat the invisible parts as solid geometry. This exact trap made
   * the clouds vanish behind every plant/tree — see `World.renderSceneDepth`.
   */
  private readonly override = new THREE.MeshBasicMaterial();
  private width = 1;
  private height = 1;

  constructor(private readonly renderer: THREE.WebGLRenderer) {
    this.texture = new THREE.DepthTexture(1, 1);
    this.texture.format = THREE.DepthFormat;
    // UnsignedInt (24-bit) — UnsignedShort's 16 bits visibly bands the fade at this scene's
    // near/far spread (0.1 / 1000).
    this.texture.type = THREE.UnsignedIntType;
    this.texture.minFilter = THREE.NearestFilter;
    this.texture.magFilter = THREE.NearestFilter;

    this.target = new THREE.WebGLRenderTarget(1, 1, {
      depthTexture: this.texture,
      depthBuffer: true,
      // No mips/filtering on the colour attachment — it's never read.
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
    });
  }

  /** Match the render target to the canvas × `scale` (call on resize / scale change). */
  setSize(width: number, height: number, scale: number): void {
    const w = Math.max(1, Math.floor(width * scale));
    const h = Math.max(1, Math.floor(height * scale));
    if (w === this.width && h === this.height) return;
    this.width = w;
    this.height = h;
    this.target.setSize(w, h);
  }

  /**
   * Capture scene depth for this frame.
   *
   * @param exclude Objects hidden for the duration of the pass. Pass anything the effect
   *   doesn't need to fade against — every excluded object is pure saved GPU time. This
   *   MUST include the water: `WaterComplex.onBeforeRender` drives a `Reflector` that
   *   re-renders the ENTIRE scene into its own target, so leaving water visible here would
   *   fire a full nested reflection render inside the depth pass, every frame, per pool.
   */
  render(
    scene: THREE.Scene,
    camera: THREE.Camera,
    exclude: readonly THREE.Object3D[] = [],
  ): void {
    const restore = exclude.map((o) => o.visible);
    for (const o of exclude) o.visible = false;

    const prevOverride = scene.overrideMaterial;
    const prevBackground = scene.background;
    scene.overrideMaterial = this.override;
    scene.background = null; // skip painting a backdrop we're going to throw away

    this.renderer.setRenderTarget(this.target);
    this.renderer.clear(true, true, false);
    this.renderer.render(scene, camera);
    this.renderer.setRenderTarget(null);

    scene.overrideMaterial = prevOverride;
    scene.background = prevBackground;
    exclude.forEach((o, i) => {
      o.visible = restore[i]!;
    });
  }

  dispose(): void {
    this.target.dispose();
    this.texture.dispose();
    this.override.dispose();
  }
}
